import type { AsyncData, AsyncDataOptions, NuxtError } from '#app'
import { storeToRefs } from 'pinia'

import { removeEmojis } from '@/utils/common'
import { AsyncDataKeys, getAsyncDataKey } from '@/constants/async-data-keys'

import { getProductComments, getProductReviewTags, getProductReviewTagsCount } from '@/service/product'
import { putReviewLike, deleteReviewLike } from '@/service/review'

import type { GetProductCommentsParams } from '@/types/product'
import type { ReviewReviewResponse } from '@/types/api'
import { ResponseCode } from '@/types/service'

import { useAuthStore } from '@/store/auth'

export function useComment(name: ReadonlyRef<string | undefined>) {
  const { t } = useI18n()

  const isMaskedOrEmptyName = computed(() => !name.value || name.value === '*')
  const displayCommenterName = computed(() => (isMaskedOrEmptyName.value ? t('member.empty') : name.value))

  // Avatar
  const firstNonEmojiLetter = computed(() => (isMaskedOrEmptyName.value ? '' : removeEmojis(name.value as string).charAt(0)))
  const isDefaultAvatarShown = computed(() => !firstNonEmojiLetter.value)
  const { localeUpperCaseString: commenterFirstLetter } = useUpperCase(firstNonEmojiLetter)

  return {
    isDefaultAvatarShown,
    commenterFirstLetter,
    displayCommenterName,
  }
}

interface UseGetProductCommentsOpts {
  debounce?: boolean
  useDual?: boolean
  asyncDataOpts?: AsyncDataOptions<ReviewReviewResponse>
}

type ProductCommentsAsyncData = AsyncData<ReviewReviewResponse | undefined, NuxtError<unknown> | undefined>

export function useGetProductComments(
  key: string,
  pid: ReadonlyRef<string | number | undefined>,
  queryParams: ComputedRef<GetProductCommentsParams>,
  opts: UseGetProductCommentsOpts & { useDual: true }
): Promise<ProductCommentsAsyncData>
export function useGetProductComments(
  key: string,
  pid: ReadonlyRef<string | number | undefined>,
  queryParams: ComputedRef<GetProductCommentsParams>,
  opts?: UseGetProductCommentsOpts & { useDual?: false }
): ProductCommentsAsyncData & { isProductCommentsLoading: ComputedRef<boolean> }
export function useGetProductComments(
  key: string,
  pid: ReadonlyRef<string | number | undefined>,
  queryParams: ComputedRef<GetProductCommentsParams>,
  { debounce = false, useDual = false, asyncDataOpts = {} }: UseGetProductCommentsOpts = {}
): Promise<ProductCommentsAsyncData> | (ProductCommentsAsyncData & { isProductCommentsLoading: ComputedRef<boolean> }) {
  const getProductCommentsWithDebounce = useDebounceFn(getProductComments, 300)
  const ussAsyncDataFunc = useDual ? useDualAsyncData : useAsyncData

  const asyncData = ussAsyncDataFunc(
    key,
    async () => {
      if (!pid.value) {
        throw new Error('pid is required')
      }

      const apiFunc = debounce ? getProductCommentsWithDebounce : getProductComments
      const { data } = await apiFunc(pid.value, queryParams.value)

      return data
    },
    { watch: [queryParams], ...asyncDataOpts }
  )

  // useDual path: useDualAsyncData is async, returns Promise<AsyncData> at runtime
  // (typed as AsyncData due to the cast in useDualAsyncData.ts)
  // Return the promise directly so [bid].vue can use it with Promise.all
  if (useDual) {
    return asyncData as unknown as Promise<ProductCommentsAsyncData>
  }

  // Non-dual path: useAsyncData returns AsyncData & Promise synchronously
  const isProductCommentsLoading = computed(() => ['idle', 'pending'].includes(asyncData.status.value))

  return { ...asyncData, isProductCommentsLoading }
}

export function useGetProductReviewTags(id: ReadonlyRef<string | undefined>) {
  const asyncData = useLazyAsyncData(
    getAsyncDataKey(AsyncDataKeys.PRODUCT_REVIEW_TAGS, [id.value]),
    async () => {
      if (!id.value) {
        return null
      }

      const res = await getProductReviewTags(id.value)

      return res.data.rating
    },
    { watch: [id], server: false }
  )

  return asyncData
}

export function useGetProductReviewTagsCount(pid: ReadonlyRef<string | number | undefined>) {
  const asyncData = useLazyAsyncData(
    getAsyncDataKey(AsyncDataKeys.PRODUCT_REVIEW_TAGS_COUNT, [pid.value]),
    async () => {
      if (!pid.value) {
        return null
      }

      const res = await getProductReviewTagsCount(pid.value)

      return res.data
    },
    { watch: [pid], server: false }
  )

  return asyncData
}

type UseLikeReviewArgs = {
  isUseful: ReadonlyRef<boolean>
  usefulCount: ReadonlyRef<number>
}

export function useLikeReview({ isUseful, usefulCount }: UseLikeReviewArgs) {
  const authStore = useAuthStore()
  const { isLogin } = storeToRefs(authStore)

  const isLiked = ref(isUseful.value)
  const previousIsLiked = ref(isLiked.value)
  const likesCount = ref(usefulCount.value)

  const likedIconName = computed(() => (isLiked.value ? 'heart' : 'heart-line'))
  const likedIconColor = computed(() => (isLiked.value ? 'red-50' : 'gray-80'))

  let controller = new AbortController()

  watch(isUseful, newIsUseful => {
    isLiked.value = newIsUseful
  })

  watch(isLogin, newIsLogin => {
    if (!newIsLogin) {
      isLiked.value = false
    }
  })

  async function fetchLikeReviewApi(id?: number | string) {
    if (typeof id === 'undefined') {
      return
    }

    const apiFunc = isLiked.value ? deleteReviewLike : putReviewLike

    controller.abort()
    controller = new AbortController()

    isLiked.value = !isLiked.value

    try {
      await apiFunc(id, { signal: controller.signal })

      if (previousIsLiked.value !== isLiked.value) {
        likesCount.value += previousIsLiked.value ? -1 : 1
        previousIsLiked.value = isLiked.value
      }
    } catch (error: any) {
      if (error?.options?.signal.aborted || error?.data?.code === ResponseCode.DUPLICATE_KEY) {
        return
      }

      isLiked.value = !isLiked.value
    }
  }

  return { isLiked, likesCount, likedIconName, likedIconColor, fetchLikeReviewApi }
}
